import { GridContainer, scrollToId, StickyBar } from '@bob/library'
import { PageContext } from '@websrc/App/AppContext'
import { AppProvider } from '@websrc/App/AppProvider'
import { Breadcrumbs } from '@websrc/App/Components/Breadcrumb'
import { Download } from '@websrc/App/Components/Download/Download'
import { FooterPrivacy } from '@websrc/App/Components/Footer/FooterPrivacy'
import { LessonSelector } from '@websrc/App/Components/Lesson/LessonSelector'
import { Sidebar } from '@websrc/App/Components/Sidebar/Sidebar'
import { TextAsset } from '@websrc/App/Components/TextAsset'
import { pathToPageName } from '@websrc/App/Helpers/pathToPageName'
import { HashChangeSmoothScrollProvider } from '@websrc/App/Hooks/hashChangeSmoothScroll'
import { useStickyBarHeightScrollMargin } from '@websrc/App/Hooks/useStickyBarHeightScrollMargin'
import { mapTeacherHub, mapTeacherHubSectionPageContent } from '@websrc/DataMappers/mapTeacherHub'
import { mapTeacherHubProducts } from '@websrc/DataMappers/mapTeacherHubProducts'
import MainTopbar from '@websrc/MainTopbar/MainTopbar'
import { Selector } from '@websrc/Spot/Selector/Selector'
import clsx from 'clsx'
import { box, rule, styles } from 'leds'
import React, { Fragment, useEffect } from 'react'
import { match } from 'ts-pattern'

// TODO: separate this into smaller components will come in another MR

export type TeacherHubRenderProps = {
  page: ReturnType<typeof mapTeacherHub>['page']
  products: ReturnType<typeof mapTeacherHubProducts>
}

export type TeacherHubSectionContentProps = {
  content: ReturnType<typeof mapTeacherHubSectionPageContent>
}

const HR = () => (
  <hr
    className={clsx(
      rule({ background: 'grey5' }),
      styles({ marginY: 30 }),
      box({ type: 'text', align: 'center' })
    )}
  />
)

export const TeacherHubSectionContent = ({ content }: TeacherHubSectionContentProps) => (
  <div className={box({ align: 'center', type: 'textWide' })}>
    {content?.map((cont, key) => (
      <Fragment key={key}>
        {match(cont)
          .with({ __dataType: 'textAsset' }, (textAsset) => <TextAsset data={textAsset} />)
          .with({ __dataType: 'selector' }, (selector) => (
            <div className={box({ type: 'text', align: 'center' })}>
              <Selector selector={selector} />
            </div>
          ))
          .with({ __dataType: 'downloadSection' }, (download) => <Download data={download} />)
          .with({ __dataType: 'lessonSelector' }, (lesson) => <LessonSelector selector={lesson} />)
          .with(null, () => null)
          .exhaustive()}
        {key !== Number(content?.length) - 1 && <HR />}
      </Fragment>
    ))}
  </div>
)

export const TeacherHubRender = ({ page, products }: TeacherHubRenderProps) => {
  useStickyBarHeightScrollMargin()

  useEffect(() => {
    // Scroll to section when page loads
    scrollToId(decodeURIComponent(window.location.hash.replace('#', '')))
  }, [])

  return (
    <>
      <StickyBar bgColor={'grayWhite'} noGutter={true}>
        <Sidebar
          navigation={page.navigation}
          products={products}
          selectedNavigation={page.selectedNavigation}
          selectedProduct={page.selectedProduct}
        />
      </StickyBar>
      <GridContainer className={styles({ display: 'flex', flexDirection: 'column' })}>
        <div className={box({ type: 'text', align: 'center' })}>
          <Breadcrumbs items={page.breadcrumb} />
        </div>
        <TeacherHubSectionContent content={page.content} />
      </GridContainer>
    </>
  )
}

export type TeacherHubPageProps = {
  pageContext: PageContext & TeacherHubRenderProps
}

const TeacherHubPage = ({ pageContext }: TeacherHubPageProps) => {
  const { appContext, page, products } = pageContext
  const [siteSection, pageName] = pathToPageName(appContext.path)

  return (
    <AppProvider
      {...appContext}
      hideFooter={true}
      hideMainTopbar={true}
      pageName={pageName}
      pageType={'teacher-hub'}
      siteSection={siteSection}
    >
      <HashChangeSmoothScrollProvider>
        <MainTopbar
          markets={appContext.markets}
          noContainer={true}
          servicebar={appContext.servicebar}
          topbar={appContext.topbar}
        />
        <TeacherHubRender page={page} products={products} />
        {appContext.footer && (
          <>
            <HR />
            <FooterPrivacy
              className={box({ type: 'text', align: 'center' })}
              cookiePolicy={appContext.cookiePolicy}
              copyright={appContext.footer.copyright}
              policy={appContext.footer.policy}
            />
          </>
        )}
      </HashChangeSmoothScrollProvider>
    </AppProvider>
  )
}

export default TeacherHubPage
